/**
 * @file index.tsx
 *
 * @fileoverview Renders the UI for the job detail. Contains 2 slots JobHeaderExtraInfo and JobBodyExtraInfo for
 * client customisations.
 */

import React, { useContext, useEffect, useState } from 'react';
import { Job } from '~/jobiqo/types/types';
import { isJobIframe } from '~/jobiqo/services/jobs';
import JobSchemaOrg from '~/jobiqo/components/Job/SchemaOrg';
import LazyHydrate from 'react-lazy-hydration';
import { useDimensions } from '~/jobiqo/hooks/useDimensions';
import { JobApplyButton } from '~/jobiqo/components/Job/Detail/components/JobApplyButton';
import { JobDetailContainer } from '~/jobiqo/components/Job/Detail/components/Container';
import BookmarkedJobsBookmark from '~/jobiqo/components/BookmarkedJobs/components/Bookmark';
import {
  extractJobDetailCommonProps,
  isCurrentPageJobSearch,
} from '~/jobiqo/components/Job/Detail/utils';
import JobDetailBody from '~/jobiqo/components/Job/Detail/components/Body';
import JobBookmarkPlacement from '~/jobiqo/components/BookmarkedJobs/enums/JobBookmarkPlacement';
import JobDetailBookmarkWrapper from '~/jobiqo/components/Job/Detail/BookmarkWrapper';
import {
  JobAlertContext,
  JobAlertContextInterface,
} from '~/jobiqo/components/JobAlert/context';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { shouldOpenJobAlertModal } from '~/jobiqo/components/JobAlert/utils';
import Image from '~/jobiqo/components/Image';
import JobDetailBreadcrumbs from '~/jobiqo/components/Job/Detail/components/Breadcrumbs';
import styles from './detailView.module.scss';
import ApplicationRestrictionMessage from '../../Apply/components/ApplicationRestrictionMessage';
import { JobDetailWrapper } from '~/vcl/organisms/03-job/01-detail/01-job-detail-wrapper';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { JobDetail } from '~/vcl/organisms/03-job/01-detail';
import { TrackingPixel } from '../components/TrackingPixel';
import { useIntl } from 'react-intl';
import { useApplicationLink } from '~/jobiqo/components/Job/Detail/hooks/useApplicationLink';
import { buildRedirectUrlWithDestination } from '~/jobiqo/lib/redirect';

export interface JobDetailViewProps {
  /**
   * The job entity.
   */
  job: Job;
  /**
   * The job logo.
   */
  logo: string;
  /**
   * If the user has permissions to edit the job.
   */
  editLink?: React.ReactNode;
  /**
   * The link to the job search page.
   */
  jobsPage: string;
  /**
   * When applicant clicks job apply button.
   */
  onApply: () => void;
  /**
   * If the job should occupy full container.
   */
  fullContainer?: boolean;
  /**
   * If the job is being previewed.
   */
  isPreview?: boolean;
  /**
   * The flag to add overflow.
   *
   * @default false
   */
  overflow?: boolean;
  /**
   * The children element.
   */
  children?: React.ReactNode;
  /**
   * If external anchors in the main job description should get rel=nofollow.
   */
  noFollowExternalLinks?: boolean;
  /**
   * If external anchors in the main job description should be rendered as plain text.
   */
  plainTextExternalLinks?: boolean;
  /**
   * Disable the standalone cover-image overlap margin.
   */
  disableCoverOverlap?: boolean;
  /**
   * Hide breadcrumbs above the job detail.
   */
  hideBreadcrumbs?: boolean;
}

const JobDetailView = ({
  job,
  editLink,
  fullContainer = false,
  isPreview = false,
  overflow = false,
  jobsPage,
  onApply,
  children,
  noFollowExternalLinks = false,
  plainTextExternalLinks = false,
  disableCoverOverlap = false,
  hideBreadcrumbs = false,
}: JobDetailViewProps) => {
  const [ref, dimensions] = useDimensions();
  const userContext: UserContextInterface = useContext(UserContext);
  const [applyButtonClicked, setApplyButtonClicked] = useState<boolean>(false);
  const { showModal, setShowModal }: JobAlertContextInterface = useContext(
    JobAlertContext,
  );
  // Store clickApply tracking pixel in states.
  const [
    showClickApplyTrackingPixel,
    setShowClickApplyTrackingPixel,
  ] = useState<boolean>(false);
  const intl = useIntl();
  const { JOBS } = useFrontendConfig();
  const JOB_TRACKING_PIXEL_ENABLED = JOBS.JOB_TRACKING_PIXEL_ENABLED;

  const isJobSearchPage = isCurrentPageJobSearch();

  // Check if the current logged in user is eligible to apply to the job.
  const applicationRestricted: boolean = !!(
    (userContext.user &&
      userContext.user.isEligibleForJobApply &&
      userContext.user.id == job.ownerId) ||
    (userContext.user && job.applicationRestricted?.restrictionStatus)
  );

  // We show iframe on desktop only for pdf and links.
  // If there is no description (parser failed) we also show iframe on mobile.
  // If the link is http and site is in http (which always is) we also can't show the iframe.
  const isIframe = isJobIframe(job);

  const handleJobApply = () => {
    // Redirect anonymous users if application restrictions is enabled.
    if (
      !userContext.user &&
      job.applicationRestricted?.restrictionStatus &&
      job.applicationWorkflow !== 'external'
    ) {
      window.open(
        buildRedirectUrlWithDestination(
          '/user/register',
          useApplicationLink(job),
        ),
        '_self',
      );
    }
    if (shouldOpenJobAlertModal(job, userContext, JOBS.JOB_ALERT.MODAL)) {
      setShowModal(true);
      setApplyButtonClicked(true);
    } else {
      onApply();
    }
    if (job['clickApplyTrackingPixel']) {
      setShowClickApplyTrackingPixel(true);
    }
  };

  useEffect(() => {
    // Only if the apply button was clicked, and the modal was
    // displayed, we can proceed with the job application.
    if (applyButtonClicked && !showModal) {
      setApplyButtonClicked(false);
      onApply();
    }
  }, [showModal]);

  return (
    <div
      className={
        !disableCoverOverlap &&
        !isJobSearchPage &&
        job.organizationProfile?.cover?.url
          ? styles.detailWrapper
          : ''
      }
    >
      <JobDetailContainer job={job} jobsPage={jobsPage}>
        <JobDetailWrapper
          ref={ref}
          type={job.type}
          overflow={overflow}
          isIframe={isIframe}
          {...(job.isHighlight
            ? {
                'data-test-id': 'highlight-job',
              }
            : null)}
        >
          {!hideBreadcrumbs && !isJobSearchPage && !isPreview && (
            <Spacing
              paddingLeft={6}
              paddingTop={7}
              paddingBottom={3}
              marginTop={0}
            >
              <JobDetailBreadcrumbs job={job} />
            </Spacing>
          )}
          <JobDetail
            fullContainer={fullContainer}
            overflow={overflow}
            verticalLayoutTablet={true}
            {...(JOBS.JOB_SEARCH_SHOW_ORG_BANNER &&
              isJobSearchPage &&
              job.organizationProfile?.cover?.url && {
                Banner: (
                  <Image
                    src={job.organizationProfile.cover.url}
                    width={job.organizationProfile.cover.width ?? 1920}
                    height={job.organizationProfile.cover.height ?? 500}
                    alt={job.organizationProfile.cover.alt ?? ''}
                  />
                ),
              })}
            titleElement={fullContainer ? 'h2' : 'h1'}
            actions={
              <JobApplyButton
                job={job}
                onApply={handleJobApply}
                applicationRestricted={applicationRestricted}
              />
            }
            withBottomMenu={!!userContext.user?.id}
            edit={editLink}
            Bookmark={
              <JobDetailBookmarkWrapper job={job}>
                <BookmarkedJobsBookmark
                  job={job}
                  placement={JobBookmarkPlacement.JOB_DETAIL}
                  bookmarkMutationCallback={(isBookmarked) => {
                    job.bookmarked = isBookmarked;
                  }}
                />
              </JobDetailBookmarkWrapper>
            }
            {...extractJobDetailCommonProps(job, intl)}
          >
            <div>
              <LazyHydrate whenIdle={true}>
                {applicationRestricted && (
                  <Spacing marginTop={0} marginBottom={7}>
                    <ApplicationRestrictionMessage
                      message={
                        job.applicationRestricted?.restrictionMessage ?? ''
                      }
                    />
                  </Spacing>
                )}
                <JobDetailBody
                  job={job}
                  isPreview={isPreview}
                  isIframe={isIframe}
                  dimensions={dimensions}
                  noFollowExternalLinks={noFollowExternalLinks}
                  plainTextExternalLinks={plainTextExternalLinks}
                />
                {job.workflowState.value === 'published' &&
                  !isJobSearchPage &&
                  !isPreview && <JobSchemaOrg job={job} />}
              </LazyHydrate>
            </div>
            {children}
            {JOB_TRACKING_PIXEL_ENABLED && (
              <>
                {job['trackingPixel'] && (
                  <TrackingPixel
                    trackingPixelCode={job['trackingPixel']}
                    jobId={job.id.toString()}
                    identifier="job-tracking-px-script"
                  />
                )}
                {showClickApplyTrackingPixel && (
                  <TrackingPixel
                    trackingPixelCode={job['clickApplyTrackingPixel']}
                    jobId={job.id.toString()}
                    identifier="job-click-apply-tracking-px-script"
                  />
                )}
              </>
            )}
          </JobDetail>
        </JobDetailWrapper>
      </JobDetailContainer>
    </div>
  );
};

export default JobDetailView;
